/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React, { useEffect } from "react"
import { Container, FlexContainer, H1, Text, Image } from "@carrotfertility/carotene"
import { Breadcrumbs } from "../../components/read/shared/Breadcrumbs"
import { MainLayout } from "components/views/app/domainIsolation/MainLayout"
import { GroupComponentProps } from "./GroupParser"
import { Group } from "./utils"
import { StackedMarkdown } from "./components/StackedMarkdown"
import { LegalFooter } from "./components/LegalFooter"
import { ContentfulConditionalGate } from "lib/contentful/components/ContentfulConditionGate"
import { FindASessionButton } from "./components/FindSessionButton"
import { CarrotPlanRequiredModal } from "components/views/app/CarrotPlanRequiredModal"
import { useCurrentUser } from "components/context/user/UserContext"
import { getHeap } from "./utils/heap"
import { Moderators } from "./components/Moderators"
import slugify from "slugify"
import { useIntl } from "react-intl"

const Topics = ({ topicsMarkdown }: { topicsMarkdown: string }): JSX.Element => (
  <Container background="lightPampas" borderRadius="8px" max-width={"320px"} height={"100%"} padding="medium">
    <StackedMarkdown source={topicsMarkdown} />
  </Container>
)

const GroupHeading = ({ overview, title }: { overview: string; title: string }): JSX.Element => (
  <FlexContainer direction="column" gap="tiny">
    <Text size="small" color={"black-60"}>
      {overview}
    </Text>
    <H1>{title}</H1>
  </FlexContainer>
)

const GroupDetails = ({ group, groupSlugId }: { group: Group; groupSlugId: string }): JSX.Element => {
  const { overview, title, description, topics, calendlyUrl } = group
  return (
    <FlexContainer direction={["column", null, null, "row"]} gap="giant">
      <FlexContainer width={[null, null, null, "70%"]} gap="large" direction="column">
        <FlexContainer gap="medium" direction="column">
          <GroupHeading overview={overview} title={title} />
          <StackedMarkdown source={description} />
        </FlexContainer>
        <Container>
          <FindASessionButton sessionPickerUrl={calendlyUrl} groupSlugId={groupSlugId} />
        </Container>
      </FlexContainer>
      <Topics topicsMarkdown={topics} />
    </FlexContainer>
  )
}

const GroupLandingPage = ({ group, subcategory, category }: GroupComponentProps): JSX.Element => {
  const { isInAppLockdown } = useCurrentUser()
  const groupSlugId = `${group.slug}-${group.id}`
  const intl = useIntl()
  useEffect(() => {
    if (isInAppLockdown) {
      getHeap().track("Carrot Plan Required Modal Rendered", {
        "Group Slug ID": groupSlugId
      })
    }
  }, [isInAppLockdown, groupSlugId])
  const categorySlug = slugify(category?.fields?.navTitle ?? category?.fields?.title)

  const groupPath = { path: `/read/${categorySlug}/group/${group.slug}`, title: group.title }
  return (
    <MainLayout>
      <ContentfulConditionalGate conditions={group.conditions}>
        <FlexContainer gap="huge" direction="column" data-group-id={groupSlugId}>
          <Breadcrumbs category={category} subcategory={subcategory} />
          <FlexContainer gap={["huge", null, null, "giant"]} direction="column">
            <Container>
              <Image
                src={group.thumbnail?.file.url}
                altText={intl.formatMessage({ defaultMessage: "Group thumbnail" })}
                height="80px"
                width="80px"
                sx={{ marginBottom: "24px", borderRadius: "8px" }}
              />
              <GroupDetails group={group} groupSlugId={groupSlugId} />
            </Container>
            <Moderators moderators={group?.moderators} groupPath={groupPath} />
            <LegalFooter />
          </FlexContainer>
        </FlexContainer>
      </ContentfulConditionalGate>
      <CarrotPlanRequiredModal />
    </MainLayout>
  )
}

export default GroupLandingPage
